/**
 * Shared constants for the WBR Callouts Viewer chatbox.
 *
 * These values back the pure helper layer (`chatHelpers.ts`), the conversation
 * state machine (`chatReducer.ts`), and the presentational component
 * (`WBRChatbox.tsx`). Keeping them in one module keeps limits, timeouts, theme
 * values, and user-facing copy consistent and easy to adjust.
 */

// ── Input / response limits ────────────────────────────────────────────────

/**
 * Maximum number of characters allowed in a question. While the trimmed input
 * exceeds this length, the Send_Action is disabled and the mutation is not
 * invoked (Req 2.6).
 */
export const MAX_QUESTION_LEN = 4000;

/**
 * Maximum number of source references displayed for an assistant message.
 * Parsed sources are capped at this count (Req 4.4).
 */
export const MAX_SOURCES = 20;

// ── Timeouts ───────────────────────────────────────────────────────────────

/**
 * Client-side timeout for an in-flight `askAI2` mutation. If no response is
 * received within this window the request is abandoned and a timeout error is
 * shown (Req 6.2). Matches the AppSync synchronous resolver ceiling (~30s).
 */
export const TIMEOUT_MS = 30_000;

/**
 * Hard backstop for the pending state. Even if the timeout race does not fire,
 * the pending state is terminated after this ceiling so the Send_Action is
 * always re-enabled (Req 5.5).
 */
export const PENDING_CEILING_MS = 60_000;

// ── Layering ───────────────────────────────────────────────────────────────

/**
 * z-index for the floating overlay so it renders on a higher visual layer than
 * the PDF pane and the callout panel (Req 8.5). Intentionally high to sit above
 * the viewer's existing content without participating in its layout.
 */
export const OVERLAY_Z_INDEX = 1300;

// ── Theme (reused from WBRCalloutsViewer) ──────────────────────────────────

/**
 * Amazon navy, reused from the viewer's theme for visual consistency (Req 8.4).
 */
export const NAVY = "#232F3E";

/**
 * Amazon accent orange, reused from the viewer's theme for visual consistency
 * (Req 8.4).
 */
export const ACCENT_ORANGE = "#FF9900";

// ── User-facing copy ───────────────────────────────────────────────────────

/**
 * Static placeholder shown while the conversation has zero messages, prompting
 * the user to enter a question (Req 1.3).
 */
export const EMPTY_STATE_PLACEHOLDER =
  "Ask a question about this WBR — for example, what changed week over week or why a metric moved.";

/**
 * Default error text shown when a question could not be answered and no
 * server-provided message is available (Req 6.4).
 */
export const DEFAULT_ERROR_TEXT =
  "Sorry, your question could not be answered. Please try again or rephrase your question.";

/**
 * Error text shown when a request does not complete within the client timeout
 * (Req 6.2).
 */
export const TIMEOUT_TEXT =
  "Your question timed out before a response was returned. Please try again.";

/**
 * Notice shown when the response succeeds but contains no answer content
 * (Req 4.5). The submitted user question is retained.
 */
export const NO_ANSWER_CONTENT_TEXT =
  "No answer content was returned for this question. Please try rephrasing it.";

/**
 * Notice shown when the chat is unavailable because the user identity is
 * missing or invalid (Req 7.2).
 */
export const IDENTITY_UNAVAILABLE_TEXT =
  "Chat is unavailable because your user identity could not be determined. Please reload the page or contact support if this persists.";
